/**
 * Plugin para implementación de GTM
 * La idea es que la implementación del tag manager sea lo menos invasiva
 * en la lógica de los componentes y vistas como sea posible
 *
 * @nota No se aplicó como provider de píxel porque necesita estar un poco
 * más embebido en la lógica de la app (ej: escuchar los blur de los campos)
 */
import get from "lodash/get";
import find from "lodash/find";
import bus from "@/bus";
import store from "@/store";
import configBrand from "../config/brands";
import * as MUTATIONS from "../config/constants/mutation-types";
import { provincias } from "@/config/constants";

const dataLayerName = "googleGTMDatalayer";

const GoogleTagManager = {
  install(Vue) {
    /**
     * Activar el GTM de acuerdo al ID por campaña
     */
    if (configBrand.googleGTMId) {
      (function (w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode.insertBefore(j, f);
        Vue.$log.info("Google GTM active", i);
      })(window, document, "script", dataLayerName, configBrand.googleGTMId);
    }

    /**
     * Helpers
     */

    const mapStateToCollection = (collection, storePath, key = "label") => {
      /**
       * Buscar en una colección por value y retornar el label
       */
      return get(
        find(collection, {
          value: get(store, storePath, "")
        }),
        key,
        ""
      ).toLowerCase();
    };

    const mapRouteNameToForm = {
      paso1: "datos del comercio"
    };

    const stripHtml = html => {
      let tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    };

    Vue.$gtmPush = data => {
      /**
       * @global Push al GTM
       */
      this._gtmPushData(data);
    };

    Vue.$gtmFormInteraction = data => {
      let formName = get(
        mapRouteNameToForm,
        get(store, "state.route.name", ""),
        ""
      );
      Vue.$gtmPush({
        event: "formInteraction",
        formName: formName,
        formFieldName: data.formFieldName,
        formFieldStatus: data.formFieldStatus
      });
    };

    Vue.$gtmUiInteraction = uiText => {
      let formName = get(
        mapRouteNameToForm,
        get(store, "state.route.name", ""),
        ""
      );
      Vue.$gtmPush({
        event: "uiInteraction",
        formName: formName,
        uiText
      });
    };

    Vue.mixin({
      methods: {
        gtmOnTextFieldBlur(formFieldName, formFieldValue) {
          /**
           * @global mixin: Interacciones con campos de texto
           */
          let formFieldStatus = formFieldValue ? "complete" : "empty";
          Vue.$gtmFormInteraction({ formFieldName, formFieldStatus });
        },

        gtmOnSelectBlur(formFieldName, formFieldValue) {
          /**
           * @global mixin: Interacciones con selects
           */
          let formFieldStatus = formFieldValue ? "selected" : "unselected";
          Vue.$gtmFormInteraction({ formFieldName, formFieldStatus });
        },

        gtmOnCheckboxBlur(formFieldName, formFieldValue) {
          /**
           * @global mixin: Interacciones con checkboxes y radios
           */
          let formFieldStatus = formFieldValue ? "checked" : "unchecked";
          Vue.$gtmFormInteraction({ formFieldName, formFieldStatus });
        },

        gtmOnLinkClick(uiText) {
          /**
           * @global mixin: Interacciones con links y botones
           */
          Vue.$gtmUiInteraction(uiText);
        },

        gtmOnSystemError(errorMessage, errorCode) {
          /**
           * @global mixin: Trackeo de errores de sistema
           */
          let errorPlace = get(
            mapRouteNameToForm,
            get(store, "state.route.from.name", ""),
            ""
          );
          Vue.$gtmPush({
            event: "error",
            errorType: "sistema",
            errorMessage: stripHtml(errorMessage),
            errorPlace,
            errorCode
          });
        },

        gtmOnUserError(errorPlace, errorMessage) {
          /**
           * @global mixin: Trackeo de errores de sistema
           */
          Vue.$gtmPush({
            event: "error",
            errorType: "usuario",
            errorMessage: stripHtml(errorMessage),
            errorPlace,
            errorCode: undefined
          });
        }
      }
    });

    bus.$on("mutation", mutationObj => {
      /**
       * Escucho todas las mutaciones en la app
       * y disparo eventos de acuerdo a c/u
       */
      let { mutation } = mutationObj;

      switch (mutation.type) {
        case MUTATIONS.ON_PASO_1:
          // Envío del paso datos personales
          this._gtmPushData({
            event: "formSubmit",
            formName: mapRouteNameToForm["paso1"],
            province: mapStateToCollection(
              provincias,
              "state.paso1.domicilioParticular.provincia"
            ),
            city: get(
              store,
              "state.paso1.domicilioParticular.localidad",
              ""
            ).toLowerCase()
          });
          break;

        case MUTATIONS.ON_GRACIAS:
          // Envío del paso consulta
          this._gtmPushData({
            event: "leadSuccess",
            tramiteId: get(store, "state.oferta.nroDeTramite", "")
          });
          break;

        default:
          break;
      }
    });
  },
  _gtmPushData(data) {
    // Método interno para pushear datos al GTM
    if (configBrand.googleGTMId) {
      window[dataLayerName] = window[dataLayerName] || [];
      window[dataLayerName].push(data);
      bus.$log.info("Google GTM push", data);
    }
  }
};

export default GoogleTagManager;
